import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/apiClient';

/**
 * The user's role and effective feature set, as the SERVER sees them.
 *
 * There is deliberately no client-side defaults table here. There used to
 * be: a DEFAULT_FEATURES map naming four roles and their grants, merged
 * under whatever the server returned, and — worse — handed out whole in the
 * .catch(). A network blip therefore promoted the caller to `cisco_user`
 * with its full feature set. The backend still gated the data, so this was
 * not a data breach, but it opened the product's UI to anyone whose
 * /api/auth/me call happened to fail, and it meant entitlements had two
 * sources of truth that drifted every time a flag was added.
 *
 * The server computes the whole map in GET /api/auth/me — role defaults from
 * feature_toggles, then per-user overrides from user_feature_flags, then the
 * super-admin and BYOK grants. Mirroring any of that here can only be wrong.
 *
 * On failure this hook fails CLOSED: no role, no features. Callers that want
 * to tell "you may not" apart from "we could not find out" should read
 * `roleError` — everything else keeps treating an unknown user as an
 * unentitled one, which is the safe reading.
 *
 * PRODUCTION-READINESS-ROADMAP.md §4 Stage 1 / §10 item 4.
 */
const EMPTY_FEATURES = Object.freeze({});

export function useRole() {
  /*
   * ONE query, shared. This was a useState + useEffect per caller, so every
   * component that wanted a role fetched /api/auth/me for itself — six callers
   * today, and three of those requests were in flight at once on /accounts.
   * react-query dedupes them to a single request and hands the rest the cache.
   *
   * `retry: 1` and a 45s apiClient timeout bound the wait. Before, the app's
   * permission gate had no way out of a slow /api/auth/me except that timeout,
   * and nothing told the reader it was waiting on anything.
   *
   * NOT PERSISTED. `meta.persist: false` keeps entitlements out of
   * localStorage — index.js's dehydrate predicate honours this flag, and auth
   * state is exactly what that opt-out was added for. A stale cached role
   * rehydrating into a fresh tab is the same failure as the DEFAULT_FEATURES
   * table this hook was written to delete.
   */
  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => apiClient.get('/api/auth/me'),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    meta: { persist: false },
  });

  // Fail CLOSED on error: an entitlement we could not read is not one we can
  // grant — the same rule middleware/auth.js follows. Unchanged in substance
  // from the .catch() this replaces.
  const data = query.isSuccess ? (query.data || {}) : {};
  const role = data.role || null;
  const isSuperAdmin = data.is_super_admin === true;
  const features = data.features || EMPTY_FEATURES;

  const isFeatureEnabled = useCallback(
    (featureKey) => features[featureKey] === true,
    [features]
  );

  return {
    role,
    roleLoading: query.isPending,
    roleError: query.isError,
    /** Re-ask. The permission gate needs a way out that is not a page reload. */
    retryRole: query.refetch,
    isAdmin: role === 'admin',
    isPaloUser: role === 'palo_user',
    isCiscoUser: role === 'cisco_user',
    isCustomer: role === 'customer',
    isSuperAdmin,
    features,
    isFeatureEnabled,
  };
}
